import { type MouseEvent, type ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconAgent,
  IconBell,
  IconBooks,
  IconBoxesStacked,
  IconBrain,
  IconClockRotateLeft,
  IconComments,
  IconFolderUser,
  IconGear,
  IconKeySkeletonLeftRight,
  IconLink,
  IconPlus,
  IconPuzzlePiece,
  IconScrewdriverWrench,
  IconServer,
  IconShieldCheck,
  IconSparkles,
  IconStore,
  IconTag,
  IconUserPlus,
  IconUserRobot,
} from '@optiaxiom/icons';
import { useDecision } from '@optimizely/react-sdk';

import { FeatureFlags } from '@frontend/constants/featureFlags';
import { OPAL_SYSTEM_INSTRUCTION_MANAGER_FEATURE_FLAG } from '@frontend/envConfig';
import { useFeatureFlag } from '@frontend/feature-flags';
import { useSpecializedAgentPermissions } from '@frontend/features/agents/components/specialized-agent/agent-form/hooks/useSpecializedAgentPermissions';
import { useWorkflowPermissions } from '@frontend/features/agents/components/workflow-agent/hooks/useWorkflowPermissions';
import { usePrivateAgentPermissions } from '@frontend/features/agents/hooks/usePrivateAgentPermissions';
import { useVAUContext } from '@frontend/features/vau/hooks/useVAUContext';
import { useConnectorTabVisibility, useInstanceId, useOperations } from '@frontend/hooks';

import { type TActionInput, useSpotlightAPI } from '../store';

import { type NavCommandData } from './types';

// Operations required to reach the org instructions / Context surfaces.
const INSTRUCTIONS_OPS = ['view_instructions', 'manage_instructions', 'use_chat'] as const;

/**
 * Build the gated navigation actions. Each action is gated to the STRICTER of its
 * left-nav/tab link visibility AND its route reachability — so the palette never offers
 * a destination the current user can't actually load. Where a link is more permissive
 * than its route guard (e.g. Plugins, whose left-nav link only needs `use_chat` but whose
 * route needs `manage_instructions`/`manage_specialized_agents`), we mirror the route so
 * the action doesn't silently redirect. Closed-gate actions are filtered out.
 *
 * Two destinations have two possible homes chosen by feature flag (agents directory,
 * teammate directory); the `to`/`visible` for those are computed once below.
 */
export function useNavigationActions(): TActionInput[] {
  const navigate = useNavigate();
  const { instanceId } = useInstanceId();
  const api = useSpotlightAPI();
  const { canUser, canInstance } = useOperations();
  const { data: vauContext } = useVAUContext();
  const isInVauMode = vauContext?.is_assumed === true;

  const vauEnabled = useFeatureFlag(FeatureFlags.VAU).enabled;
  const messagingEnabled = useFeatureFlag(FeatureFlags.MESSAGING_ENABLED).enabled;
  const pluginsEnabled = useFeatureFlag(FeatureFlags.PLUGINS).enabled;
  const brandsEnabled = useFeatureFlag(FeatureFlags.BRANDS).enabled;
  const inputGuardrailsEnabled = useFeatureFlag(FeatureFlags.INPUT_GUARDRAILS).enabled;
  const directoryDecision = useFeatureFlag(FeatureFlags.DIRECTORY);
  const directoryEnabled = directoryDecision.enabled;
  // The teammate directory lives under /directory only when the DIRECTORY flag's `vau`
  // variable is on (mirrors DirectoryPage's `teammatesEnabled`).
  const teammatesDirEnabled = Boolean(directoryDecision.variables.vau);
  // Neither flag is in the typed catalog — read them via useDecision, as
  // ContextPage/InstructionPage do, so the gates stay keyed to the same flags.
  const personalFilesEnabled = useDecision('user_context_files')[0].enabled;
  const systemInstructionsEnabled = useDecision(OPAL_SYSTEM_INSTRUCTION_MANAGER_FEATURE_FLAG)[0]
    .enabled;

  // Connectors sub-tab visibility, shared with ToolsManagement so the two never drift.
  const conn = useConnectorTabVisibility();

  // Create-agent gates already compose the right capability + permission checks.
  const { canCreateSpecializedAgents } = useSpecializedAgentPermissions();
  const { canCreateWorkflowAgents } = useWorkflowPermissions();
  const { canManagePrivateAgents } = usePrivateAgentPermissions();

  return useMemo(() => {
    const hrefFor = (to: string) => `/instances/${instanceId}${to}`;

    // The link owns mouse clicks; `execute` owns the keyboard. Stop propagation either
    // way so the row's click handler never double-fires.
    const linkClick = (to: string) => (e: MouseEvent) => {
      // Modified click → new tab: let the anchor behave like any real link and leave
      // the palette open. (Middle-click fires `auxclick`, not this handler, so it too
      // opens a new tab and keeps the palette open.)
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        e.stopPropagation();
        return;
      }
      // Plain click → same-tab navigation: take over from the anchor and close.
      e.preventDefault();
      e.stopPropagation();
      navigate(hrefFor(to));
      api.close();
    };

    const nav = (
      key: string,
      title: string,
      icon: ReactElement,
      to: string,
      keywords: string[] = ['go', 'navigate', 'open']
    ): TActionInput => ({
      key,
      type: 'navigation',
      group: 'navigation',
      label: title,
      keywords,
      data: { title, icon, to, onClick: linkClick(to) } satisfies NavCommandData,
      execute: (ctx, spotlight) => {
        // Any modifier (Cmd/Ctrl/Shift) → new tab, palette stays open — mirrors a
        // modified mouse click, where the native anchor already opens elsewhere.
        if (ctx.modifiers.cmd || ctx.modifiers.ctrl || ctx.modifiers.shift) {
          window.open(hrefFor(to), '_blank', 'noopener,noreferrer');
          return true;
        }
        navigate(hrefFor(to));
        spotlight.close();
      },
    });

    const canInstructions = canUser([...INSTRUCTIONS_OPS]);
    const canVau = vauEnabled && canUser(['manage_vt', 'view_agents']) && !isInVauMode;
    const canCreateSpecialized =
      canUser(['view_agents']) && (canCreateSpecializedAgents || canManagePrivateAgents);
    const canCreateWorkflow =
      canUser(['view_agents']) && (canCreateWorkflowAgents || canManagePrivateAgents);
    // Plugins mirrors its ROUTE guard (stricter than the left-nav link's `use_chat`).
    const canPlugins =
      pluginsEnabled && canUser(['manage_instructions', 'manage_specialized_agents']);

    // Agents directory: the DIRECTORY flag chooses the home; both need view_agent_directory
    // plus the host route's own permission.
    const agentsDirectoryTo = directoryEnabled ? '/directory/agents' : '/agents/agent-directory';
    const canAgentsDirectory =
      canUser(['view_agent_directory']) &&
      (directoryEnabled ? canUser(['use_chat']) : canUser(['view_agents']));

    // Teammate directory: prefer /directory/teammates (needs the DIRECTORY `vau` variable),
    // otherwise the in-VAU-section /vaus/directory.
    const teammatesPref = directoryEnabled && teammatesDirEnabled && canUser(['use_chat']);
    const vausDirReachable =
      vauEnabled && canUser(['manage_vt', 'view_agents']) && canUser(['use_chat']) && !isInVauMode;
    const teammateDirectoryTo = teammatesPref ? '/directory/teammates' : '/vaus/directory';

    const descriptors: Array<{ visible: boolean; action: TActionInput }> = [
      {
        visible: canUser(['use_chat']),
        action: nav('go-to-chat', 'Go to chat', <IconComments />, '/chat'),
      },
      {
        visible: messagingEnabled && canUser(['use_chat']),
        action: nav('go-to-messages', 'Go to team messages', <IconComments />, '/messages', [
          'messages',
          'team',
        ]),
      },
      {
        visible: canUser(['use_chat']),
        action: nav('go-to-artifacts', 'Go to artifacts', <IconBooks />, '/artifacts'),
      },
      {
        visible: canUser(['use_chat']),
        action: nav('go-to-notifications', 'Go to notifications', <IconBell />, '/notifications', [
          'alerts',
        ]),
      },
      {
        visible: canUser(['use_chat']),
        action: nav('go-to-settings', 'Go to settings', <IconGear />, '/settings', [
          'preferences',
          'appearance',
        ]),
      },
      {
        visible: canInstructions,
        action: nav('go-to-skills', 'Go to skills', <IconSparkles />, '/context', [
          'skills',
          'instructions',
          'context',
        ]),
      },
      {
        visible: systemInstructionsEnabled && canInstructions,
        action: nav(
          'go-to-system-skills',
          'Go to system skills',
          <IconSparkles />,
          '/context/system-skills',
          ['system', 'skills', 'instructions']
        ),
      },
      {
        visible: personalFilesEnabled && canInstructions,
        action: nav(
          'go-to-personal-files',
          'Go to personal files',
          <IconFolderUser />,
          '/context/personal-files',
          ['files', 'context']
        ),
      },
      {
        visible: !personalFilesEnabled && canInstructions,
        action: nav('go-to-memory', 'Go to memory', <IconBrain />, '/context/memory', ['context']),
      },
      {
        visible: brandsEnabled && canInstructions,
        action: nav('go-to-brands', 'Go to brands', <IconTag />, '/context/brands', [
          'brand',
          'brand packs',
        ]),
      },
      {
        visible: canInstructions,
        action: nav('create-skill', 'Create new skill', <IconPlus />, '/skills/create/personal', [
          'new',
          'skill',
          'create',
        ]),
      },
      {
        visible: systemInstructionsEnabled && canUser(['manage_instructions']),
        action: nav(
          'create-system-skill',
          'Create new system skill',
          <IconPlus />,
          '/skills/create/system',
          ['new', 'system', 'skill', 'create']
        ),
      },
      {
        visible: canUser(['view_agents']),
        action: nav('go-to-agents', 'Go to agents', <IconAgent />, '/agents'),
      },
      {
        visible:
          canInstance(['workflows_full_enabled', 'workflows_chat_enabled']) &&
          canUser(['view_agents']),
        action: nav(
          'go-to-agent-logs',
          'Go to agent activity logs',
          <IconClockRotateLeft />,
          '/agents/executions',
          ['executions', 'activity', 'logs', 'workflow']
        ),
      },
      {
        visible: canAgentsDirectory,
        action: nav(
          'go-to-agents-directory',
          'Go to agents directory',
          <IconStore />,
          agentsDirectoryTo,
          ['directory', 'marketplace', 'agents']
        ),
      },
      {
        visible:
          inputGuardrailsEnabled &&
          canInstance(['input_guardrails_enabled']) &&
          canUser(['manage_chat_config', 'manage_specialized_agents']),
        action: nav(
          'go-to-input-guardrails',
          'Go to agent input guardrails',
          <IconShieldCheck />,
          '/agents/input-guardrails',
          ['guardrails', 'input', 'safety']
        ),
      },
      {
        visible: canCreateSpecialized,
        action: nav(
          'create-specialized-agent',
          'Create new specialized agent',
          <IconPlus />,
          '/agents/specialized/create',
          ['new', 'specialized', 'agent', 'create']
        ),
      },
      {
        visible: canCreateWorkflow,
        action: nav(
          'create-workflow-agent',
          'Create new workflow agent',
          <IconPlus />,
          '/agents/workflow/create',
          ['new', 'workflow', 'agent', 'create']
        ),
      },
      {
        visible: canVau,
        action: nav('go-to-vaus', 'Go to virtual teammates', <IconUserRobot />, '/vaus', [
          'vau',
          'virtual teammates',
          'virtual agentic users',
        ]),
      },
      {
        visible: canVau,
        action: nav('create-vau', 'Create a new virtual teammate', <IconUserPlus />, '/vaus/new', [
          'new',
          'virtual teammate',
          'vau',
          'create',
        ]),
      },
      {
        visible: teammatesPref || vausDirReachable,
        action: nav(
          'go-to-vau-directory',
          'Go to virtual teammate directory',
          <IconStore />,
          teammateDirectoryTo,
          ['directory', 'virtual teammates', 'vau']
        ),
      },
      {
        // Mirrors the left-nav Connectors gate exactly; lands on the Connectors default
        // tab (not a sub-tab, which needs its own permission and would silently redirect).
        visible: canUser(['view_tools', 'use_chat']),
        action: nav('go-to-connectors', 'Go to connectors', <IconLink />, '/connectors', [
          'mcp',
          'tools',
          'connectors',
        ]),
      },
      {
        visible: conn.canViewTools,
        action: nav('go-to-tools', 'Go to tools', <IconScrewdriverWrench />, '/connectors/tools', [
          'tools',
        ]),
      },
      {
        visible: conn.canViewRegistry,
        action: nav(
          'go-to-registries',
          'Go to registries',
          <IconBoxesStacked />,
          '/connectors/registries',
          ['registries', 'registry']
        ),
      },
      {
        visible: conn.canViewExternalProviders,
        action: nav(
          'go-to-external-providers',
          'Go to external providers',
          <IconKeySkeletonLeftRight />,
          '/connectors/external-providers',
          ['external', 'providers', 'auth']
        ),
      },
      {
        visible: conn.canViewMCPServers,
        action: nav(
          'go-to-mcp-servers',
          'Go to MCP servers',
          <IconServer />,
          '/connectors/mcp-servers',
          ['mcp', 'servers', 'remote']
        ),
      },
      {
        visible: canPlugins,
        action: nav('go-to-plugins', 'Go to plugins', <IconPuzzlePiece />, '/plugins'),
      },
      {
        // The plugins directory tab exists only when the standalone /directory is off.
        visible: canPlugins && !directoryEnabled,
        action: nav(
          'go-to-plugins-directory',
          'Go to plugins directory',
          <IconStore />,
          '/plugins/directory',
          ['directory', 'plugins']
        ),
      },
      {
        visible: directoryEnabled && canUser(['use_chat']),
        action: nav(
          'go-to-skills-directory',
          'Go to skills directory',
          <IconStore />,
          '/directory/skills',
          ['directory', 'skills']
        ),
      },
    ];

    return descriptors
      .filter(descriptor => descriptor.visible)
      .map(descriptor => descriptor.action);
  }, [
    api,
    brandsEnabled,
    canCreateSpecializedAgents,
    canCreateWorkflowAgents,
    canInstance,
    canManagePrivateAgents,
    canUser,
    conn,
    directoryEnabled,
    inputGuardrailsEnabled,
    instanceId,
    isInVauMode,
    messagingEnabled,
    navigate,
    personalFilesEnabled,
    pluginsEnabled,
    systemInstructionsEnabled,
    teammatesDirEnabled,
    vauEnabled,
  ]);
}
